import { X, Bike, Users, MapPin, Sparkles, Mail, ChevronRight, Send } from 'lucide-react'
import { useState } from 'react'
import { sendContactEmail } from '../../services/emailService'

interface AboutModalProps {
  isOpen: boolean
  onClose: () => void
}

export function AboutModal({ isOpen, onClose }: AboutModalProps) {
  const [showContactForm, setShowContactForm] = useState(false)
  const [formData, setFormData] = useState({
    name: '',
    organization: '',
    email: '',
    message: ''
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitMessage, setSubmitMessage] = useState('')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)
    setSubmitMessage('')
    
    try {
      const result = await sendContactEmail({
        name: formData.name,
        organization: formData.organization || undefined,
        email: formData.email,
        message: formData.message
      })
      
      if (result.success) {
        setSubmitMessage('Thank you for your interest! We\'ll be in touch soon.')
        setFormData({ name: '', organization: '', email: '', message: '' })
        setTimeout(() => {
          setShowContactForm(false)
          setSubmitMessage('')
        }, 3000)
      } else {
        setSubmitMessage(result.error || 'Failed to send message. Please try again.')
      }
    } catch (error) {
      console.error('Contact form error:', error)
      setSubmitMessage('An error occurred. Please try again or email us directly.')
    } finally {
      setIsSubmitting(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-gray-800 rounded-xl shadow-2xl w-full max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
        {/* Compact Header */}
        <div className="p-4 border-b border-gray-700 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-emerald-500/10 rounded-lg">
              <Bike className="w-5 h-5 text-emerald-400" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-white">About CYA Routes</h1>
              <p className="text-gray-400 text-sm">Community-driven route curation</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-gray-700 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          <div className="p-5 space-y-4">
            {/* Compact Story Card */}
            <div className="bg-gradient-to-r from-gray-900/60 to-gray-900/40 border border-gray-700 rounded-lg p-4">
              <h2 className="text-base font-semibold text-white mb-3 flex items-center">
                <Sparkles className="w-4 h-4 text-emerald-400 mr-2" />
                Our Story
              </h2>
              <p className="text-gray-300 text-sm leading-relaxed mb-3">
                Inspired by BikeRoutes, CYA Routes leverages modern technology to create a sophisticated platform 
                for route discovery and community building. We empower local cycling organizations to showcase their 
                expertise and encourage bike tourism.
              </p>
              <div className="bg-emerald-500/10 border border-emerald-500/20 rounded p-2">
                <p className="text-emerald-400 text-xs font-medium">
                  Mission: De-clutter the routes scene through community curation
                </p>
              </div>
            </div>

            {/* Tourism Partnership Card */}
            <div className="bg-gradient-to-r from-cyan-500/10 to-emerald-500/10 border border-cyan-500/30 rounded-lg p-4 mb-3">
              <h2 className="text-base font-semibold text-white mb-3 flex items-center">
                <Users className="w-4 h-4 text-cyan-400 mr-2" />
                Tourism Partnership Program
              </h2>
              <p className="text-gray-300 text-sm leading-relaxed mb-3">
                We aim to partner with tourism organizations to help drive cycling tourism to your region. Our platform 
                showcases your area's best routes, connects visitors with local businesses, and provides the tools 
                to promote sustainable bike tourism.
              </p>
              <div className="bg-cyan-500/10 border border-cyan-500/20 rounded p-2">
                <p className="text-cyan-400 text-xs font-medium">
                  Let's work together to bring more cyclists to your region
                </p>
              </div>
            </div>

            {/* How It Works - Compact Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="bg-gray-900/40 border border-gray-700 rounded-lg p-4 hover:border-emerald-500/30 transition-colors">
                <div className="flex items-start">
                  <div className="p-1.5 bg-emerald-500/10 rounded mr-3">
                    <Users className="w-4 h-4 text-emerald-400" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-sm font-semibold text-white mb-2">For Tourism Organizations</h3>
                    <ul className="text-gray-400 text-xs space-y-1">
                      <li className="flex items-start">
                        <ChevronRight className="w-3 h-3 text-gray-600 mr-1 mt-0.5 flex-shrink-0" />
                        <span>Drive cycling tourism to your region</span>
                      </li>
                      <li className="flex items-start">
                        <ChevronRight className="w-3 h-3 text-gray-600 mr-1 mt-0.5 flex-shrink-0" />
                        <span>Connect visitors with local businesses</span>
                      </li>
                      <li className="flex items-start">
                        <ChevronRight className="w-3 h-3 text-gray-600 mr-1 mt-0.5 flex-shrink-0" />
                        <span>Analytics on route usage & visitor patterns</span>
                      </li>
                      <li className="flex items-start">
                        <ChevronRight className="w-3 h-3 text-gray-600 mr-1 mt-0.5 flex-shrink-0" />
                        <span>Promote accommodations & attractions</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="bg-gray-900/40 border border-gray-700 rounded-lg p-4 hover:border-cyan-500/30 transition-colors">
                <div className="flex items-start">
                  <div className="p-1.5 bg-cyan-500/10 rounded mr-3">
                    <MapPin className="w-4 h-4 text-cyan-400" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-sm font-semibold text-white mb-2">For Riders</h3>
                    <ul className="text-gray-400 text-xs space-y-1">
                      <li className="flex items-start">
                        <ChevronRight className="w-3 h-3 text-gray-600 mr-1 mt-0.5 flex-shrink-0" />
                        <span>Find curated routes</span>
                      </li>
                      <li className="flex items-start">
                        <ChevronRight className="w-3 h-3 text-gray-600 mr-1 mt-0.5 flex-shrink-0" />
                        <span>Surface analysis</span>
                      </li>
                      <li className="flex items-start">
                        <ChevronRight className="w-3 h-3 text-gray-600 mr-1 mt-0.5 flex-shrink-0" />
                        <span>Connect locally</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Subscription Tiers Info */}
            <div className="bg-gradient-to-r from-purple-500/10 to-purple-500/5 border border-purple-500/20 rounded-lg p-4 mb-3">
              <h2 className="text-sm font-semibold text-white mb-2 flex items-center">
                <Sparkles className="w-4 h-4 text-purple-400 mr-2" />
                Platform Access
              </h2>
              <p className="text-gray-300 text-xs leading-relaxed mb-2">
                Core features are free for all users. Premium features like Google Places integration for 
                finding nearby cafes, accommodations, and services require a subscription to help cover 
                third-party API costs.
              </p>
              <div className="text-purple-400 text-xs">
                <span className="font-medium">Coming soon:</span> Subscription tiers for enhanced features
              </div>
            </div>

            {/* Features & Contact Row */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {/* Key Features */}
              <div className="bg-gradient-to-br from-gray-900/60 to-gray-900/40 border border-gray-700 rounded-lg p-4">
                <h2 className="text-sm font-semibold text-white mb-3 flex items-center">
                  <Sparkles className="w-4 h-4 text-cyan-400 mr-2" />
                  Key Features
                </h2>
                <div className="space-y-1.5 text-xs text-gray-300">
                  <div className="flex items-center">
                    <div className="w-1.5 h-1.5 bg-cyan-400 rounded-full mr-2"></div>
                    <span>3D terrain visualization</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-1.5 h-1.5 bg-emerald-400 rounded-full mr-2"></div>
                    <span>Real-time surface analysis</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-1.5 h-1.5 bg-cyan-400 rounded-full mr-2"></div>
                    <span>Intelligent route classification</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-1.5 h-1.5 bg-emerald-400 rounded-full mr-2"></div>
                    <span>Community curation</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-1.5 h-1.5 bg-purple-400 rounded-full mr-2"></div>
                    <span>Premium: Google Places integration</span>
                  </div>
                </div>
              </div>

              {/* Contact */}
              <div className="bg-gradient-to-br from-emerald-500/10 to-emerald-500/5 border border-emerald-500/20 rounded-lg p-4">
                <h2 className="text-sm font-semibold text-white mb-3 flex items-center">
                  <Mail className="w-4 h-4 text-emerald-400 mr-2" />
                  Get In Touch
                </h2>
                <p className="text-xs text-gray-300 mb-3">
                  Tourism organizations, cycling clubs, and route enthusiasts - let's work together!
                </p>
                <button
                  onClick={() => setShowContactForm(true)}
                  className="inline-flex items-center text-emerald-400 hover:text-emerald-300 text-xs font-medium transition-colors px-3 py-1.5 bg-emerald-500/10 hover:bg-emerald-500/20 rounded-lg border border-emerald-500/30"
                >
                  <Send className="w-3 h-3 mr-1.5" />
                  Send us a message
                  <ChevronRight className="w-3 h-3 ml-1" />
                </button>
              </div>
            </div>

            {/* Footer */}
            <div className="text-center pt-3 border-t border-gray-700">
              <p className="text-gray-500 text-xs">© 2025 Vincent Masci</p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Contact Form Modal */}
      {showContactForm && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
          <div className="bg-gray-800 rounded-xl shadow-2xl w-full max-w-md">
            {/* Form Header */}
            <div className="p-4 border-b border-gray-700 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-white">Contact Us</h3>
              <button
                onClick={() => {
                  setShowContactForm(false)
                  setSubmitMessage('')
                }}
                className="p-1.5 hover:bg-gray-700 rounded-lg transition-colors"
              >
                <X className="w-4 h-4 text-gray-400" />
              </button>
            </div>
            
            {/* Form Content */}
            <form onSubmit={handleSubmit} className="p-5">
              <div className="space-y-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-300 mb-1.5">
                    Your Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-900/50 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                    placeholder="John Doe"
                  />
                </div>
                
                <div>
                  <label htmlFor="organization" className="block text-sm font-medium text-gray-300 mb-1.5">
                    Organization
                  </label>
                  <input
                    type="text"
                    id="organization"
                    value={formData.organization}
                    onChange={(e) => setFormData({ ...formData, organization: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-900/50 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                    placeholder="Tourism Board / Cycling Club"
                  />
                </div>
                
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-1.5">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-900/50 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                    placeholder="john@example.com"
                  />
                </div>
                
                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-300 mb-1.5">
                    Message *
                  </label>
                  <textarea
                    id="message"
                    required
                    rows={4}
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-900/50 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm resize-none"
                    placeholder="Tell us about your organization and how we can work together..."
                  />
                </div>
              </div>
              
              {/* Submit Message */}
              {submitMessage && (
                <div className={`mt-4 p-3 rounded-lg ${
                  submitMessage.includes('Thank you') 
                    ? 'bg-emerald-500/10 border border-emerald-500/30' 
                    : 'bg-red-500/10 border border-red-500/30'
                }`}>
                  <p className={`text-sm ${
                    submitMessage.includes('Thank you') ? 'text-emerald-400' : 'text-red-400'
                  }`}>
                    {submitMessage}
                  </p>
                </div>
              )}
              
              {/* Form Actions */}
              <div className="mt-6 flex gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowContactForm(false)
                    setSubmitMessage('')
                  }}
                  className="flex-1 px-4 py-2 border border-gray-700 rounded-lg text-gray-300 hover:bg-gray-700 transition-colors text-sm font-medium"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex-1 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 rounded-lg text-white font-medium transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Send Message
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}